import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { RETURN_STATUSES, STATUS_TIMELINE } from './returnConstants';

/**
 * Horizontal progress bar for the canonical happy-path of a return:
 *   pending → approved → shipped → received → completed
 *
 * Off-path terminal states (rejected / cancelled) get a single
 * banner instead of a step.
 */
const ReturnTimeline = ({ status }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    if (status === 'rejected' || status === 'cancelled') {
        const meta = RETURN_STATUSES[status];
        return (
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: (t) => `1px solid ${t.palette[meta.color === 'default' ? 'divider' : `${meta.color}.main`]}`,
                    bgcolor: (t) => meta.color === 'default'
                        ? 'transparent'
                        : `${meta.color}.lighter`,
                }}
            >
                <Typography
                    variant="body2"
                    fontWeight={700}
                    color={`${meta.color}.dark`}
                >
                    {isArabic ? meta.labelAr : meta.labelEn}
                </Typography>
            </Box>
        );
    }

    const currentIdx = STATUS_TIMELINE.indexOf(status);
    const reachedIdx = currentIdx === -1 ? 0 : currentIdx;

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{ width: '100%', overflowX: 'auto' }}
        >
            {STATUS_TIMELINE.map((step, idx) => {
                const meta = RETURN_STATUSES[step];
                const reached = idx <= reachedIdx;
                const active = idx === reachedIdx;
                return (
                    <React.Fragment key={step}>
                        <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 64 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: reached ? 'primary.main' : 'action.disabledBackground',
                                    color: reached ? 'primary.contrastText' : 'text.secondary',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    border: active ? '2px solid' : '0',
                                    borderColor: 'primary.dark',
                                }}
                            >
                                {reached ? <Check sx={{ fontSize: 16 }} /> : idx + 1}
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: active ? 700 : 500,
                                    color: reached ? 'text.primary' : 'text.secondary',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {isArabic ? meta.labelAr : meta.labelEn}
                            </Typography>
                        </Stack>
                        {idx < STATUS_TIMELINE.length - 1 && (
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 2,
                                    mx: 0.5,
                                    bgcolor: idx < reachedIdx ? 'primary.main' : 'action.disabledBackground',
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Stack>
    );
};

export default ReturnTimeline;
